<template>
  <div class="page-view">
    <div class="header-row">
      <div>
        <h2>应用管理</h2>
        <p>管理应用推送订单的接入信息、类型、状态和创建时间。</p>
      </div>
      <el-button type="primary" @click="openCreate">新增应用</el-button>
    </div>

    <div class="brand-card filter-card">
      <div class="toolbar-row">
        <el-input v-model="filters.keyword" placeholder="应用名称 / appID" clearable />
        <el-select v-model="filters.app_type" placeholder="应用类型" clearable>
          <el-option v-for="option in appTypeOptions" :key="option" :label="option" :value="option" />
        </el-select>
        <el-select v-model="filters.is_active" placeholder="状态" clearable>
          <el-option label="启用" :value="true" />
          <el-option label="停用" :value="false" />
        </el-select>
        <el-button type="primary" plain @click="handleQuery">查询</el-button>
      </div>
    </div>

    <div class="brand-card table-card">
      <el-table :data="items" stripe>
        <el-table-column prop="name" label="应用名称" min-width="180" />
        <el-table-column prop="app_type" label="应用类型" width="110" />
        <el-table-column prop="app_id" label="appID" min-width="200" />
        <el-table-column prop="app_secret" label="应用密钥" min-width="260" />
        <el-table-column prop="created_by_name" label="创建人" min-width="120" />
        <el-table-column v-if="currentUser.role === 'super_admin'" label="可用管理员" min-width="220">
          <template slot-scope="{ row }">{{ (row.admin_names || []).join("、") || "-" }}</template>
        </el-table-column>
        <el-table-column label="状态" width="100">
          <template slot-scope="{ row }">{{ row.is_active ? "启用" : "停用" }}</template>
        </el-table-column>
        <el-table-column prop="created_at" label="创建时间" min-width="180" />
        <el-table-column label="操作" width="280" fixed="right">
          <template slot-scope="{ row }">
            <template v-if="canManageItem(row)">
              <el-button size="mini" @click="openEdit(row)">编辑</el-button>
              <el-button size="mini" plain @click="toggleStatus(row)">
                {{ row.is_active ? "停用" : "启用" }}
              </el-button>
              <el-button size="mini" type="danger" plain @click="removeItem(row)">删除</el-button>
            </template>
            <span v-else class="readonly-text">仅可使用</span>
          </template>
        </el-table-column>
      </el-table>
      <div class="pager-row">
        <el-pagination
          background
          layout="total, prev, pager, next, sizes"
          :total="total"
          :page-size="pager.page_size"
          :current-page="pager.page"
          :page-sizes="[10, 20, 50]"
          @current-change="onPageChange"
          @size-change="onSizeChange"
        />
      </div>
    </div>

    <el-dialog :title="editingId ? '编辑应用' : '新增应用'" :visible.sync="showDialog" width="540px">
      <el-form :model="form" label-width="100px">
        <el-form-item label="应用名称">
          <el-input v-model.trim="form.name" />
        </el-form-item>
        <el-form-item label="应用类型">
          <el-select v-model="form.app_type" placeholder="请选择应用类型">
            <el-option v-for="option in appTypeOptions" :key="option" :label="option" :value="option" />
          </el-select>
        </el-form-item>
        <el-form-item label="appID">
          <el-input v-model.trim="form.app_id" :disabled="!!editingId" />
        </el-form-item>
        <el-form-item label="应用密钥">
          <el-input v-model.trim="form.app_secret" :disabled="!canEditSecret || !editingId" placeholder="新增自动生成，超管编辑时可修改" />
        </el-form-item>
        <el-form-item v-if="canEditAdminsInForm" label="可用管理员">
          <el-checkbox-group v-model="form.admin_ids">
            <el-checkbox v-for="leader in leaders" :key="leader.id" :label="leader.id">
              {{ leader.name }}
            </el-checkbox>
          </el-checkbox-group>
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="showDialog = false">取消</el-button>
        <el-button type="primary" @click="submitForm">提交</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { createUpstreamApp, deleteUpstreamApp, fetchLeaders, fetchUpstreamApps, updateUpstreamApp } from "../api/orders";
import { getStoredUser } from "../utils/auth";

const appTypeOptions = ["上游", "下游"];
const emptyForm = () => ({ name: "", app_type: "上游", app_id: "", app_secret: "", admin_ids: [], created_by_role: "" });

export default {
  data() {
    return {
      currentUser: getStoredUser(),
      appTypeOptions,
      filters: { keyword: "", app_type: "", is_active: "" },
      items: [],
      leaders: [],
      total: 0,
      pager: { page: 1, page_size: 10 },
      showDialog: false,
      editingId: null,
      form: emptyForm(),
    };
  },
  computed: {
    canEditSecret() {
      return this.currentUser && this.currentUser.role === "super_admin";
    },
    canEditAdminsInForm() {
      return this.currentUser.role === "super_admin" && (!this.editingId || this.form.created_by_role === "super_admin");
    },
  },
  mounted() {
    this.loadLeaders();
    this.loadItems();
  },
  methods: {
    async loadLeaders() {
      if (this.currentUser.role !== "super_admin") return;
      const { data } = await fetchLeaders();
      this.leaders = data || [];
    },
    async loadItems() {
      const { data } = await fetchUpstreamApps({
        keyword: this.filters.keyword || undefined,
        app_type: this.filters.app_type || undefined,
        is_active: this.filters.is_active === "" ? undefined : this.filters.is_active,
        page: this.pager.page,
        page_size: this.pager.page_size,
      });
      this.items = (data.items || []).map((item) => ({
        ...item,
        app_type: item.app_type || "上游",
        admin_ids: [...(item.admin_ids || [])],
      }));
      this.total = data.total || 0;
    },
    handleQuery() {
      this.pager.page = 1;
      this.loadItems();
    },
    onPageChange(page) {
      this.pager.page = page;
      this.loadItems();
    },
    onSizeChange(size) {
      this.pager.page = 1;
      this.pager.page_size = size;
      this.loadItems();
    },
    openCreate() {
      this.editingId = null;
      this.form = emptyForm();
      this.showDialog = true;
    },
    openEdit(row) {
      this.editingId = row.id;
      this.form = {
        name: row.name,
        app_type: row.app_type || "上游",
        app_id: row.app_id,
        app_secret: row.app_secret,
        admin_ids: [...(row.admin_ids || [])],
        created_by_role: row.created_by_role || "",
      };
      this.showDialog = true;
    },
    canManageItem(row) {
      return this.currentUser.role === "super_admin" || row.created_by_id === this.currentUser.id;
    },
    async submitForm() {
      try {
        const payload = { ...this.form };
        delete payload.created_by_role;
        if (!this.canEditAdminsInForm) {
          delete payload.admin_ids;
        }
        if (this.editingId) {
          await updateUpstreamApp(this.editingId, {
            name: payload.name,
            app_type: payload.app_type,
            ...(this.canEditSecret ? { app_secret: payload.app_secret } : {}),
            ...(payload.admin_ids ? { admin_ids: payload.admin_ids } : {}),
          });
          this.$message.success("更新成功");
        } else {
          await createUpstreamApp(payload);
          this.$message.success("创建成功");
        }
        this.showDialog = false;
        this.loadItems();
      } catch (error) {
        this.$message.error(error.response?.data?.detail || "提交失败");
      }
    },
    async toggleStatus(row) {
      try {
        await updateUpstreamApp(row.id, { is_active: !row.is_active });
        this.$message.success("状态已更新");
        this.loadItems();
      } catch (error) {
        this.$message.error(error.response?.data?.detail || "更新失败");
      }
    },
    async removeItem(row) {
      await this.$confirm(`确认删除应用 ${row.name} 吗？`, "删除应用", { type: "warning" });
      await deleteUpstreamApp(row.id);
      this.$message.success("删除成功");
      this.loadItems();
    },
  },
};
</script>

<style scoped>
:deep(.el-checkbox-group) {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 18px;
}

:deep(.el-checkbox) {
  margin-right: 0;
}

.readonly-text {
  color: #8b97aa;
  font-size: 13px;
}
</style>
